import {getTaggedLogger} from "./taggedLogging";

export const STORAGE_CACHE_KEYS = {
  // All of these will be NUKED when param RESET=1 is present
  SESSION_FACTS: "sessionFacts",
  PAGE_TRIGGERS: "pageTriggers",
  KLAVIYO_FORMS: "klaviyoFormHistory",
  TRIGGER_HISTORY: "triggerHistory",
  EMAIL_USER: "aag_user", // legacy key from days of long ago
}

const CACHE = {};

/**
 * Write an object to the cache and to localStorage
 *
 * @param key
 * @param object
 * @param expiresAt time to expire in seconds
 * @param expiresIn ...or, Date at which to expire
 * @param storage  localStorage (default) or sessionStorage
 * @param logger  tagged logger, or will use its own
 * @param verbose  log nothing when false
 * @returns {{value: {}, expiresAt: *, storedAt: Date}|null}
 */
export function writeCachedLocalStorage(key, object, {
  expiresAt, expiresIn, storage, logger, verbose = false
} = {}) {

  if (!key) {
    console.error("writeCachedLocalStorage: key is blank!");
    return null;
  }


  logger ||= _getLogger(verbose);

  storage ||= globalThis.localStorage;

  let now = new Date();

  expiresAt = fixExpiresAt({expiresAt, expiresIn, now});

  let data = {
    storedAt: now,
    expiresAt,
    value: Object.assign({}, object)
  }


  CACHE[key] = data;
  storage.setItem(key, JSON.stringify(data));
  logger.info(`updating storage ${key}: ${JSON.stringify(data)}`);
  return data;
}

export function deleteFromCachedLocalStorage(key, {storage, logger, verbose} = {}) {
  storage ||= globalThis.localStorage;
  logger ||= _getLogger(verbose);

  CACHE[key] = null;
  storage.removeItem(key);
  logger.info(`Deleted ${key} from cache and storage`);
}


export function readCachedLocalStorage(key, {defaultValue = null, storage, verbose = false, logger} = {}) {
  logger ||= _getLogger(verbose);

  if (!key) {
    console.error("readCachedLocalStorage: key is blank!");
    return null;
  }

  // Read from local cache.
  let data = CACHE[key];
  if (data && cachedValueGood(data)) {
    logger.info(`returning cached value for ${key}`)
    return data.value;
  }

  storage ||= globalThis.localStorage;

  // read from local storage.
  let json = storage.getItem(key);

  if (json) {
    try {
      data = JSON.parse(json);
    } catch (err) {
      logger.error(`reading cache: ${err}`)
    }

    if (cachedValueGood(data)) {
      CACHE[key] = data;

      logger.log(`${key} found, will cache & return ${typeof data.value}`)

      return data.value;
    } else {
      logger.log(`${key} expired, will return default`)

      localStorage.removeItem(key);
    }
  }

  return defaultValue;
}

globalThis.writeCachedLocalStorage = writeCachedLocalStorage;
globalThis.readCachedLocalStorage = readCachedLocalStorage;
globalThis.deleteFromCachedLocalStorage = deleteFromCachedLocalStorage;


// Called by EyeInTheSkyStartupWrapper when RESET param is present,
// this will delete anything with known keys
export function resetLocalStorageCache() {
  // to iterate keys you can do this:
  // let copy = Object.assign({}, localStorage);
  // keys = Object.keys(copy)
  let keys = Object.values(STORAGE_CACHE_KEYS)
  for (let k of keys) {
    console.log(`RESET: deleting ${k}`);
    deleteFromCachedLocalStorage(k);
  }
}

/**
 * Filter for the write parameters; compute expiresAt from any combination
 * of expiresAt, expiresIn, now.
 *
 * @param expiresAt
 * @param expiresIn
 * @param now
 * @returns {number}
 */
function fixExpiresAt({expiresAt, expiresIn, now}) {
  if (!expiresAt) {
    // use expiresIn (or a reasonable default) to calc an expiresAt
    now ||= new Date();
    expiresIn ||= 3600 * 24 * 7;  // default: one week
    expiresAt = now.getTime() + (1000 * expiresIn);
  }

  // accept an integer or a Date
  if (expiresAt.getTime)
    expiresAt = expiresAt.getTime();
  else
    expiresAt = +expiresAt;

  return expiresAt;
}


function cachedValueGood(data, now = null) {
  if (!data?.expiresAt) return false;
  // check expire time
  now ||= new Date();

  return (now.getTime() < data.expiresAt);
}

function _getLogger(verbose) {
  return verbose ? getTaggedLogger('cachedLocalStorage') : getTaggedLogger(null);
}
